"use client";

import { c as _c } from "react/compiler-runtime";
import { Eye, Shield, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FloatingIcons } from "./floating-icons";
import { ImageSlot } from "./image-slot";
type FeatureConfig = {
  key: string;
  titleKey: string;
  descKey: string;
  color: string;
  bg: string;
  imageId: string;
  imageSrc: string;
  imagePrompt: string;
};
const FEATURES: FeatureConfig[] = [{
  key: "priceCompare",
  titleKey: "landing.features.priceCompare",
  descKey: "landing.features.priceCompareDesc",
  color: "border-teal-100",
  bg: "bg-teal-50/30",
  imageId: "feature-price-compare",
  imageSrc: "/images/feature-price-compare.webp",
  imagePrompt: "3D rendered illustration: two large floating price tags in teal and gray, connected by glowing comparison arrows. The teal tag has a lower price with a 3D checkmark badge. Smooth rounded shapes, soft volumetric lighting, realistic soft shadows, glass morphism elements. Clean white background. Consistent Pixar-like 3D render quality. No text."
}, {
  key: "trends",
  titleKey: "landing.features.trends",
  descKey: "landing.features.trendsDesc",
  color: "border-teal-100/60",
  bg: "bg-teal-50/20",
  imageId: "feature-trends",
  imageSrc: "/images/feature-trends.webp",
  imagePrompt: "3D rendered illustration: a floating holographic analytics chart with a smooth glowing curve line, stylized 3D pharmaceutical capsules placed as data points along the line. The line shows ups and downs. Soft purple/violet gradient accents, volumetric lighting, realistic soft shadows. Clean white background. Consistent Pixar-like 3D render quality. No text."
}, {
  key: "coverage",
  titleKey: "landing.features.coverage",
  descKey: "landing.features.coverageDesc",
  color: "border-cyan-100",
  bg: "bg-cyan-50/30",
  imageId: "feature-coverage",
  imageSrc: "/images/feature-covarage.webp",
  imagePrompt: "3D rendered illustration: stylized 3D map of Uzbekistan in soft sky blue, slightly raised/extruded, with 13 glowing pin dots at regional capitals connected by thin light beams. Small 3D pharmacy building icons at some dots. Floating above a subtle circular platform. Volumetric lighting, realistic soft shadows. Clean white background. Consistent Pixar-like 3D render quality. No text."
}, {
  key: "realtime",
  titleKey: "landing.features.realtime",
  descKey: "landing.features.realtimeDesc",
  color: "border-teal-100/80",
  bg: "bg-teal-50/40",
  imageId: "feature-realtime",
  imageSrc: "/images/feature-realtime.webp",
  imagePrompt: "3D rendered illustration: a glossy 3D Telegram paper airplane icon in blue, with 3D Excel/spreadsheet file icons flowing from it into a stylized processing funnel/pipeline machine. Clean organized 3D data cards with medicine icons emerge from the other end. Amber/warm accent colors, volumetric lighting, realistic soft shadows. Clean white background. Consistent Pixar-like 3D render quality. No text."
}];
export function FeaturesSection() {
  const $ = _c(25);
  const {
    t
  } = useTranslation();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <FloatingIcons items={[{
      icon: Zap,
      className: "top-16 left-[6%]",
      color: "text-amber-400/50",
      animation: "animate-float-fast"
    }, {
      icon: Shield,
      className: "top-1/3 right-[4%]",
      color: "text-teal-400/50",
      animation: "animate-float-slow"
    }, {
      icon: Eye,
      className: "bottom-20 left-[9%]",
      color: "text-cyan-400/50",
      animation: "animate-float-medium"
    }]} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="pointer-events-none absolute inset-0"><div className="absolute top-1/3 right-0 hidden h-100 w-125 rounded-full bg-teal-50/40 blur-[100px] sm:block" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("landing.features.badge");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <span className="mb-4 inline-block rounded-full border border-slate-200/60 bg-slate-50/80 px-4 py-1.5 text-xs font-medium tracking-wide text-slate-600 uppercase">{t2}</span>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("landing.features.title1");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t) {
    t5 = t("landing.features.title2");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <span className="text-teal-600">{t5}</span>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t6) {
    t7 = <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:mt-4 sm:text-4xl">{t4}{" \u2014 "}{t6}</h2>;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t3 || $[16] !== t7) {
    t8 = <div className="mb-8 text-center sm:mb-14">{t3}{t7}</div>;
    $[15] = t3;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t) {
    t9 = FEATURES.map((t10, index) => {
      const {
        key,
        titleKey,
        descKey,
        color,
        bg,
        imageId,
        imageSrc,
        imagePrompt
      } = t10;
      const isReversed = index % 2 === 1;
      return <div key={key} className={`grid items-center gap-5 rounded-xl border ${color} ${bg} p-5 sm:gap-8 sm:rounded-2xl sm:p-8 lg:grid-cols-2 lg:gap-12 lg:p-12`}><div className={isReversed ? "lg:order-2" : ""}><h3 className="text-xl font-bold text-slate-900 sm:text-2xl">{t(titleKey)}</h3><p className="mt-3 text-base leading-relaxed text-slate-500">{t(descKey)}</p></div><div className={isReversed ? "lg:order-1" : ""}><ImageSlot id={imageId} className="h-40 w-full sm:h-64" src={imageSrc} prompt={imagePrompt} /></div></div>;
    });
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9) {
    t10 = <div className="space-y-6">{t9}</div>;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t8) {
    t11 = <section id="features" className="relative overflow-hidden px-4 py-14 sm:px-6 sm:py-24">{t0}{t1}<div className="relative mx-auto max-w-7xl">{t8}{t10}</div></section>;
    $[22] = t10;
    $[23] = t8;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
}